import React, {
  useRef,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
  ReactNodeArray,
} from 'react';
import { debounce, cloneDeep } from 'lodash';
import produce, { Draft, Immutable } from 'immer';

import {
  BlurWrapper,
  DeviceTypeWrapper,
  ElementBox,
  HeatmapCanvas,
  HeatmapTooltipClick,
  HeatmapTooltipHover,
  PageScreenShotImg,
  PageScreenShotWrapper,
  CrossHairTop,
  CrossHairBottom,
  CrossHairLeft,
  CrossHairRight,
  HeatmapTooltipThumbnailWrapper,
  HeatmapTooltipSplit,
  HeatmapTooltipDataRow,
  HeatmapTooltipDataTitle,
  HeatmapTooltipDataValue,
  HeatmapTooltipThumbnailImg,
  ElementCircle,
  ElementLineSVG,
  ElementCirclePlus,
  ExtraElementItem,
  ExtraElementWrapper,
  ExtraElementWrapperInner,
} from './styled';

import { uxAnalyticsState } from '../../../modules/PageAnalytics/UXAnalytics/types';
import { PageElementsFlowPayload } from '../../../api/pageAnalytics/uxAnalytics/mouseflow/type';

import errorImg from '../../../assets/images/icon-noimg@3x.png';

type elementBoxSetType = Immutable<
  {
    main: number;
    extra: PageElementsFlowPayload[];
  }[]
>;

interface MouseEventHeatmapState {
  eventCoordinates: {
    x: number;
    y: number;
    value: number;
    radius: number;
  }[];
  convertEventData: { [key: string]: { [key: string]: number } };
  elementBoxSet: {
    main: number;
    extra: PageElementsFlowPayload[];
  }[];
}
interface MouseEventHeatmapProps {
  pageElementData: PageElementsFlowPayload[] | null;
  targetRank: number;
  pageId: string;
  tooltipInfoMouseFlow: uxAnalyticsState['tooltipInfoMouseFlow'];
  onSetTargetRank: (target_rank: number) => void;
  onSetTooltipInfoMouseFlow: (
    info: uxAnalyticsState['tooltipInfoMouseFlow']
  ) => void;
}

const MouseFlowHeatmap = ({
  pageElementData,
  targetRank,
  pageId,
  tooltipInfoMouseFlow,
  onSetTargetRank,
  onSetTooltipInfoMouseFlow,
}: MouseEventHeatmapProps) => {
  // state
  const HeatmapWrapperEl = useRef<HTMLDivElement>(null);
  const pageScreenshotImg = useRef<HTMLImageElement>(null);
  const [isImgLoad, setIsImgLoad] = useState(false);
  const [elementBoxData, setElementBoxData] = useState<
    MouseEventHeatmapProps['pageElementData'] | null
  >(null);
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const [hoverTooltipCoordinate, setHoverTooltipCoordinate] = useState({
    isOpenTooltip: false,
    left: 0,
    top: 0,
    tooltipCase: 0,
  });
  const [isMouseOverTargetElement, setIsMouseOverTargetElement] = useState<
    boolean | null
  >(null);
  const [clickTargetRank, setClickTargetRank] = useState(-1);
  const [isMouseClickPlus, setIsMouseClickPlus] = useState(false);
  const [clickPlusRank, setClickPlusRank] = useState(-1);
  const [isMouseOverExtraElement, setIsMouseOverExtraElement] = useState(false);
  const [elementBoxSet, setElementBoxSet] = useState<elementBoxSetType>([]);
  const [max, setMax] = useState(0);

  const handleImageLoaded = () => {
    setIsImgLoad(true);
  };

  const handleImageLoadStart = () => {
    setIsImgLoad(false);
  };

  const calculateElementCoordinates = (imgWidth: number, imgHeight: number) => {
    const temp: MouseEventHeatmapProps['pageElementData'] = [];
    if (pageElementData && pageElementData.length > 0) {
      pageElementData.forEach((element) => {
        // if (
        //   element.element.x !== 0 &&
        //   element.element.y !== 0 &&
        //   element.element.width !== 1 &&
        //   element.element.height !== 1
        // ) {
        const x = element.element.x * imgWidth;
        const y = element.element.y * imgHeight;
        const width = element.element.width * imgWidth;
        const height = element.element.height * imgHeight;
        const pushdata = {
          click_cnt: element.click_cnt,
          rate: element.rate,
          rank: element.rank,
          duration: element.duration,
          session_cnt: element.session_cnt,
          element: {
            x,
            y,
            width,
            height,
            element_path: element.element.element_path,
            depth: element.element.depth,
          },
        };

        temp.push(pushdata);
        // }
      });
    }
    if (temp.length > 0) {
      setElementBoxData(temp);
    }
  };

  const onSetTooltipCoordinate = (
    elementX: number,
    elementY: number,
    elementWidth: number,
    elementHeight: number
  ) => {
    let tooltipCase = 0;
    let left = elementX - 4;
    let top = elementY + elementHeight + 12;
    const tooltipWidth = 211;
    const tooltipHeight = 229;
    if (elementX > imgSize.width / 2) {
      left = elementX - tooltipWidth + elementWidth;
      // left = -tooltipWidth + elementWidth;
      tooltipCase += 1;
    }
    if (elementY > imgSize.height / 2) {
      top = elementY - tooltipHeight;
      tooltipCase += 10;
    }

    setHoverTooltipCoordinate({
      isOpenTooltip: true,
      left,
      top,
      tooltipCase,
    });
  };

  const onMouseLeaveElementBox = () => {
    setClickTargetRank(-1);
    // onSetTargetRank(-1);
    // setIsMouseOverTargetElement(false);
    // setIsMouseClickPlus(false);
    setHoverTooltipCoordinate({
      isOpenTooltip: false,
      left: 0,
      top: 0,
      tooltipCase: 0,
    });
  };

  const onClickTargetElement = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    element: {
      rate: number;
      session_cnt: number;
      click_cnt: number;
      duration: number;
      rank: number;
      element: {
        x: number;
        y: number;
        width: number;
        height: number;
        depth: number;
        element_path: string;
      };
    }
  ) => {
    onSetTooltipInfoMouseFlow({
      duration: element.duration,
      pathId: element.element.element_path,
      rank: element.rank,
      rate: element.rate,
    });
    setIsMouseOverTargetElement(true);
    setClickTargetRank(element.rank);
    onSetTargetRank(element.rank);
  };

  const onClickExtraItem = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    element: {
      rate: number;
      session_cnt: number;
      click_cnt: number;
      duration: number;
      rank: number;
      element: {
        x: number;
        y: number;
        width: number;
        height: number;
        depth: number;
        element_path: string;
      };
    },
    index: number
  ) => {
    onSetTooltipInfoMouseFlow({
      duration: element.duration,
      pathId: element.element.element_path,
      rank: element.rank,
      rate: element.rate,
    });
    onSetTargetRank(element.rank);
    setIsMouseOverExtraElement(true);
    setElementBoxSet(
      produce((draft: Draft<elementBoxSetType>) => {
        draft[index].main = element.rank;
      })
    );
  };

  const CalcOvelapCircle = () => {
    if (elementBoxData) {
      let maxTemp = 0;
      elementBoxData.forEach((element) => {
        maxTemp = Math.max(maxTemp, element.duration);
      });

      setMax(maxTemp);
      const overlapCircleData = cloneDeep(elementBoxData);
      const elementBoxSetTemp: MouseEventHeatmapState['elementBoxSet'] = [];

      overlapCircleData.forEach((element, index) => {
        let size = (element.duration / maxTemp) * 140;
        if (size < 40) {
          size = 40;
        }
        const r1 = size / 2;
        elementBoxSetTemp.push({
          main: element.rank,
          extra: [element],
        });

        overlapCircleData.forEach((element2) => {
          if (element.rank !== element2.rank) {
            let size2 = (element2.duration / maxTemp) * 140;
            if (size2 < 40) {
              size2 = 40;
            }
            const r2 = size2 / 2;
            const x1 = element.element.x + element.element.width / 2;
            const x2 = element2.element.x + element2.element.width / 2;
            const y1 = element.element.y + element.element.height / 2;
            const y2 = element2.element.y + element2.element.height / 2;

            const isOverap =
              Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2) <= Math.max(r1, r2);

            if (isOverap) {
              let isAlreadySet = false;
              elementBoxSetTemp.forEach((element3) => {
                element3.extra.some((element4) => {
                  if (element4.rank === element2.rank) {
                    isAlreadySet = true;
                    return true;
                  }
                  return false;
                });
              });

              if (!isAlreadySet) {
                elementBoxSetTemp[index].extra.push(element2);
              }
            }
          }
        });
      });

      const overOvelapCircleRank: number[] = [];
      elementBoxSetTemp.forEach((element) => {
        element.extra.forEach((elementExtra) => {
          if (element.main !== elementExtra.rank) {
            elementBoxSetTemp.forEach((element2) => {
              if (element2.main === elementExtra.rank) {
                overOvelapCircleRank.push(element2.main);
              }
            });
          }
        });
      });

      const result = elementBoxSetTemp.filter((element) => {
        let mainNode = true;

        overOvelapCircleRank.forEach((elementOverlapRank) => {
          if (elementOverlapRank === element.main) {
            mainNode = false;
          }
        });

        return mainNode;
      });

      setElementBoxSet(result);
    }
  };

  const GenerateElementBox = () => {
    const result: ReactNode[] = [];

    if (elementBoxSet.length > 0) {
      elementBoxSet.forEach((element, index) => {
        const extraElementArr: ReactNodeArray = [];
        const extraElementInnerArr: ReactNodeArray = [];
        const mainElement = element.extra.filter(
          (extraEl) => extraEl.rank === element.main
        );
        const extraElement = element.extra.filter(
          (extraEl) => extraEl.rank !== element.main
        );
        extraElement.forEach((extraElementItem, indexItem) => {
          const extraNode = (
            <ExtraElementItem
              id={`voda_mouseflow_node${extraElementItem.rank}`}
              key={`voda_mouseflow_node${extraElementItem.rank}`}
              onClick={(event) => {
                event.stopPropagation();
                onClickExtraItem(event, extraElementItem, index);
                setIsMouseClickPlus(false);
              }}
            >
              {extraElementItem.rank}
            </ExtraElementItem>
          );
          extraElementArr.push(extraNode);
          if (indexItem !== 0 && indexItem % 4 === 0) {
            extraElementInnerArr.push(
              <ExtraElementWrapperInner>
                {extraElementArr.map((exElementRow, indexRow) => {
                  if (indexRow < indexItem && indexRow >= indexItem - 4) {
                    return exElementRow;
                  }
                  return null;
                })}
              </ExtraElementWrapperInner>
            );
          }
        });

        if (extraElementArr.length % 5 !== 0) {
          extraElementInnerArr.push(
            <ExtraElementWrapperInner>
              {extraElementArr.map((exElementRow, indexRow) => {
                if (
                  indexRow < extraElementArr.length &&
                  indexRow >=
                    extraElementArr.length - (extraElementArr.length % 4)
                ) {
                  return exElementRow;
                }
                return null;
              })}
            </ExtraElementWrapperInner>
          );
        }

        mainElement.forEach((extraMainElement) => {
          let size = (extraMainElement.duration / max) * 140;

          if (size < 40) {
            size = 40;
          }

          const node = (
            <ElementCircle
              $isTarget={targetRank === extraMainElement.rank}
              $x={extraMainElement.element.x}
              $y={extraMainElement.element.y}
              $width={extraMainElement.element.width}
              $height={extraMainElement.element.height}
              $rank={extraMainElement.rank}
              $size={size}
              id={`voda_mouseflow_node${extraMainElement.rank}`}
              key={`voda_mouseflow_node${extraMainElement.rank}`}
              onClick={(event) => {
                onClickTargetElement(event, extraMainElement);
                setIsMouseClickPlus(false);
              }}
            >
              {extraMainElement.rank}
              {element.extra.length > 1 ? (
                <ElementCirclePlus
                  onClick={(event) => {
                    event.stopPropagation();
                    setClickPlusRank(extraMainElement.rank);
                    setIsMouseClickPlus(true);
                    onMouseLeaveElementBox();
                  }}
                >
                  <div>{element.extra.length - 1}+</div>
                  <ExtraElementWrapper
                    $isOpen={
                      extraMainElement.rank === clickPlusRank &&
                      isMouseClickPlus
                    }
                  >
                    {extraElementInnerArr}
                  </ExtraElementWrapper>
                </ElementCirclePlus>
              ) : null}
            </ElementCircle>
          );
          result.push(node);
        });
      });
    }

    return result;
  };

  const GenerateElementLine = () => {
    const result: ReactNodeArray = [];
    if (elementBoxData) {
      elementBoxData.forEach((element, index) => {
        if (index < elementBoxData.length - 1) {
          const d = `M ${element.element.x + element.element.width / 2} ${
            element.element.y + element.element.height / 2
          } L ${
            elementBoxData[index + 1].element.x +
            elementBoxData[index + 1].element.width / 2
          } ${
            elementBoxData[index + 1].element.y +
            elementBoxData[index + 1].element.height / 2
          }`;
          result.push(
            <path
              key={`element-line-${element.rank}`}
              d={d}
              stroke="#f5ab35"
              strokeDasharray="5"
              strokeWidth="2"
              fill="none"
            />
          );
        }
      });
    }
    return result;
  };

  const PrintElementLine = useMemo(() => {
    if (pageElementData && pageElementData.length > 0) {
      return GenerateElementLine();
    }
    return false;
  }, [imgSize, pageElementData, clickTargetRank, isMouseOverTargetElement]);

  const TargetElementMemo = useMemo(() => {
    if (elementBoxSet.length > 0) {
      return GenerateElementBox();
    }
    return false;
  }, [
    imgSize,
    elementBoxSet,
    clickTargetRank,
    isMouseOverTargetElement,
    isMouseClickPlus,
    isMouseOverExtraElement,
    clickPlusRank,
    targetRank,
  ]);

  useEffect(() => {
    window.addEventListener(
      'resize',
      debounce(() => {
        // window.location.reload();
      }, 500)
    );
    return () => {
      onSetTargetRank(-1);
    };
  }, []);

  useEffect(() => {
    let isExtraCircle = true;
    elementBoxSet.some((element) => {
      if (element.main === targetRank) {
        isExtraCircle = false;

        return true;
      }
      return false;
    });

    if (isExtraCircle) {
      let extraCircleIndex = 0;
      elementBoxSet.some((element, index) => {
        let isFindExCircle = false;
        element.extra.some((element2) => {
          if (element2.rank === targetRank) {
            isFindExCircle = true;
            return true;
          }
          return false;
        });
        if (isFindExCircle) {
          extraCircleIndex = index;
          return true;
        }
        return false;
      });
      setElementBoxSet(
        produce((draft: Draft<elementBoxSetType>) => {
          if (draft[extraCircleIndex]) {
            draft[extraCircleIndex].main = targetRank;
          }
        })
      );
    }
  }, [targetRank]);

  useEffect(() => {
    const { current } = HeatmapWrapperEl;
    if (targetRank !== -1 && current) {
      const targetCircle = document.getElementById(
        `voda_mouseflow_node${targetRank}`
      );
      if (targetCircle) {
        const parent = targetCircle.parentElement;
        let parentLeft = 0;
        let parentTop = 0;
        const targetLeft = targetCircle.getBoundingClientRect().left;
        const targetTop = targetCircle.getBoundingClientRect().top;
        const targetWidth = targetCircle.getBoundingClientRect().width;
        const targetHeight = targetCircle.getBoundingClientRect().height;
        let targetRelativeLeft = targetLeft;
        let targetRelativeTop = targetTop;
        if (parent) {
          parentLeft = parent.getBoundingClientRect().left;
          targetRelativeLeft = targetLeft - parentLeft;
          parentTop = parent.getBoundingClientRect().top;
          targetRelativeTop = targetTop - parentTop;
        }
        onSetTooltipCoordinate(
          targetRelativeLeft,
          targetRelativeTop,
          targetWidth,
          targetHeight
        );
      }
    }
  }, [elementBoxSet, targetRank]);

  useEffect(() => {
    CalcOvelapCircle();
  }, [elementBoxData]);

  useEffect(() => {
    if (isImgLoad && pageScreenshotImg.current) {
      const width = pageScreenshotImg.current.offsetWidth;
      const height = pageScreenshotImg.current.offsetHeight;
      setImgSize({ width, height });
      calculateElementCoordinates(width, height);
    }
  }, [isImgLoad, pageScreenshotImg, pageElementData]);

  return (
    <PageScreenShotWrapper ref={HeatmapWrapperEl}>
      <PageScreenShotImg
        src={`https://voda-media.nerdfactory.ai/${localStorage.getItem(
          'voda_tenant'
        )}/auto/${pageId}/${encodeURIComponent('page_screenshot')}`}
        ref={pageScreenshotImg}
        onLoad={handleImageLoaded}
        onLoadStart={handleImageLoadStart}
      />
      <BlurWrapper />

      {isImgLoad &&
      imgSize.width !== 0 &&
      imgSize.height !== 0 &&
      elementBoxData &&
      elementBoxData.length > 0 &&
      pageElementData &&
      pageElementData.length > 0
        ? TargetElementMemo
        : null}

      {isImgLoad &&
      imgSize.width !== 0 &&
      imgSize.height !== 0 &&
      elementBoxData &&
      elementBoxData.length > 0 &&
      pageElementData &&
      pageElementData.length > 0 ? (
        <ElementLineSVG>{PrintElementLine}</ElementLineSVG>
      ) : null}

      {/* mouse over 시 나오는 툴팁 */}
      <HeatmapTooltipHover {...hoverTooltipCoordinate}>
        <HeatmapTooltipThumbnailWrapper>
          <HeatmapTooltipThumbnailImg
            src={`https://voda-media.nerdfactory.ai/${localStorage.getItem(
              'voda_tenant'
            )}/auto/${pageId}/${encodeURIComponent(
              tooltipInfoMouseFlow.pathId
            )}`}
            onError={(event) => {
              const target = event.target as HTMLImageElement;
              target.src = errorImg;
            }}
          />
        </HeatmapTooltipThumbnailWrapper>
        <HeatmapTooltipSplit />
        <HeatmapTooltipDataRow>
          <HeatmapTooltipDataTitle>관심 순위</HeatmapTooltipDataTitle>
          <HeatmapTooltipDataValue>
            {tooltipInfoMouseFlow.rank}위
          </HeatmapTooltipDataValue>
        </HeatmapTooltipDataRow>
        <HeatmapTooltipDataRow>
          <HeatmapTooltipDataTitle>평균 체류 시간</HeatmapTooltipDataTitle>
          <HeatmapTooltipDataValue>
            {tooltipInfoMouseFlow.duration.toFixed(2)}초
          </HeatmapTooltipDataValue>
        </HeatmapTooltipDataRow>
        <HeatmapTooltipDataRow>
          <HeatmapTooltipDataTitle>관심 비율</HeatmapTooltipDataTitle>
          <HeatmapTooltipDataValue>
            {tooltipInfoMouseFlow.rate.toFixed(2)}%
          </HeatmapTooltipDataValue>
        </HeatmapTooltipDataRow>
      </HeatmapTooltipHover>
    </PageScreenShotWrapper>
  );
};

export default MouseFlowHeatmap;
